// Turns a list of records into a cumulative growth series for the dashboard
// sparklines. Nothing here is synthesised - every point is a count of rows that
// already existed at that instant, taken from each table's own created_time.

// /devices returns an ISO timestamp (the SQL runs to_timestamp on it) while the
// other endpoints hand back the raw epoch-millisecond bigint, so both shapes
// have to be understood here.
export const toMillis = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;

  const text = String(value).trim();
  if (!text) return null;
  if (/^\d+$/.test(text)) return Number(text);

  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? null : parsed;
};

const labelFor = (millis, spanMs) => {
  const date = new Date(millis);
  // Under ~3 months of history the month name repeats on every point, so the
  // day is what actually distinguishes them.
  const useDay = spanMs < 1000 * 60 * 60 * 24 * 90;
  return date.toLocaleString("en-US", {
    month: "short",
    year: "numeric",
    ...(useDay ? { day: "numeric" } : {}),
  });
};

// A fixed 12-month window left most of these metrics flat, because their rows
// all predate it. The window instead spans from the oldest record to now, so
// each card shows the growth that actually happened.
export const cumulativeSeries = (rows, points = 12, field = "created_time") => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const stamps = rows
    .map((row) => toMillis(row?.[field]))
    .filter((value) => value !== null)
    .sort((a, b) => a - b);

  if (stamps.length === 0) return [];

  const first = stamps[0];
  const now = Date.now();
  // Everything created at the same instant, or timestamps in the future, leave
  // nothing meaningful to plot.
  const last = Math.max(now, stamps[stamps.length - 1]);
  const span = last - first;
  if (span <= 0) return [];

  const step = span / (points - 1);
  const series = [];

  for (let i = 0; i < points; i += 1) {
    const at = first + step * i;
    series.push({
      label: labelFor(at, span),
      value: stamps.filter((stamp) => stamp <= at).length,
    });
  }

  return series;
};

// True when the series actually moves. A flat line says nothing, so the card
// prints a plain sentence instead of drawing one.
export const hasVariation = (series) =>
  Array.isArray(series) && new Set(series.map((point) => point.value)).size > 1;
